<template>
  <footer class="bg-gray-900 text-gray-300">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div class="grid grid-cols-1 md:grid-cols-4 gap-8 text-center md:text-left">
        <!-- About Section -->
        <div class="col-span-1 md:col-span-2 md:text-left">
          <div class="flex flex-col md:flex-row items-center md:items-start space-y-2 md:space-y-0 md:space-x-2 mb-4">
            <div class="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            <span class="text-2xl font-bold text-white">AnyGoodDeeds</span>
          </div>
          <p class="text-gray-400 mb-4">
            A transparency-focused charity platform making impact visible, spending traceable, and generosity accountable across Africa.
          </p>
          <div class="flex space-x-4">
            <a href="#" class="text-gray-400 hover:text-primary-500 transition">
              <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            <a href="#" class="text-gray-400 hover:text-primary-500 transition">
              <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
            <a href="#" class="text-gray-400 hover:text-primary-500 transition">
              <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
          </div>
        </div>

        <!-- Quick Links -->
        <div>
          <h3 class="text-white font-bold mb-4">Quick Links</h3>
          <ul class="space-y-2">
            <li>
              <router-link to="/projects" class="hover:text-primary-500 transition">
                Browse Projects
              </router-link>
            </li>
            <li>
              <router-link to="/login" class="hover:text-primary-500 transition">
                Sign In
              </router-link>
            </li>
            <li>
              <router-link to="/register" class="hover:text-primary-500 transition">
                Get Started
              </router-link>
            </li>
            <li v-if="authStore.isAuthenticated">
              <router-link to="/dashboard" class="hover:text-primary-500 transition">
                Dashboard
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Transparency -->
        <div>
          <h3 class="text-white font-bold mb-4">Transparency</h3>
          <ul class="space-y-2">
            <li>
              <a href="/api/transparency/platform" target="_blank" class="hover:text-primary-500 transition">
                Platform Report
              </a>
            </li>
            <li>
              <router-link to="/how-we-work" class="hover:text-primary-500 transition">
                How We Work
              </router-link>
            </li>
            <li>
              <router-link to="/our-impact" class="hover:text-primary-500 transition">
                Our Impact
              </router-link>
            </li>
            <li>
              <router-link to="/faq" class="hover:text-primary-500 transition">
                FAQ
              </router-link>
            </li>
          </ul>
        </div>
      </div>

      <!-- Bottom Bar -->
      <div class="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center text-center md:text-left">
        <p class="text-gray-400 text-sm">
          © {{ currentYear }} AnyGoodDeeds. All rights reserved.
        </p>
        <div class="flex flex-wrap justify-center md:justify-start space-x-6 mt-4 md:mt-0">
          <router-link to="/privacy-policy" class="text-gray-400 hover:text-primary-500 text-sm transition">
            Privacy Policy
          </router-link>
          <router-link to="/terms-of-service" class="text-gray-400 hover:text-primary-500 text-sm transition">
            Terms of Service
          </router-link>
          <router-link to="/contact" class="text-gray-400 hover:text-primary-500 text-sm transition">
            Contact Us
          </router-link>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'

const authStore = useAuthStore()

const currentYear = computed(() => new Date().getFullYear())
</script>
